import { useEffect, useState, useRef } from "react";
import { Search, Plus, Users, FileText, Edit2, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../services/api";
import ClientForm from "../components/ClientForm";
import Swal from "sweetalert2";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  // ✅ ESTADOS DE PAGINACIÓN
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  // ✅ CATÁLOGO CAT-022 - Tipos de documento
  const getTipoDocumentoNombre = (codigo) => {
    const tipos = {
      '36': 'NIT',
      '13': 'DUI',
      '37': 'Otro',
      '03': 'Pasaporte',
      '02': 'Carnet de Residente'
    };
    return tipos[codigo] || codigo;
  };

  // ✅ Búsqueda en el backend
  const fetchClients = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const res = await api.get(`/clients/paginated?page=${page}&search=${searchTerm}`);
      
      if (res.data.data) {
        setClients(res.data.data);
        setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page);
        setTotal(res.data.total);
        setFrom(res.data.from);
        setTo(res.data.to);
      } else {
        setClients(Array.isArray(res.data) ? res.data : res.data.data || []);
      }
    } catch (error) {
      console.error("Error cargando clientes:", error);
      Swal.fire("Error", "No se pudieron cargar los clientes", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();

    // autofocus en el buscador al cargar
    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  // ✅ Función para cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchClients(newPage, search);
    }
  };

  // ✅ Manejar búsqueda con debounce
  const handleSearch = (value) => {
    setSearch(value);
    
    // Limpiar timeout anterior
    clearTimeout(window.searchTimeout);
    
    // Buscar después de 500ms de que el usuario dejó de escribir
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchClients(1, value);
    }, 500);
  };

  // Eliminar cliente
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Eliminar cliente?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/clients/${id}`);
      Swal.fire({
        icon: "success",
        title: "Cliente eliminado",
        text: "El cliente fue eliminado correctamente",
        confirmButtonText: "Aceptar",
      });
      fetchClients(currentPage, search);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo eliminar el cliente",
      });
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* CARD PRINCIPAL */}
      <div className="card">
        {/* HEADER */}
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <Users size={28} color="#667eea" />
            <h1 className="card-title">Gestión de Clientes</h1>
          </div>

          {!showForm && (
            <button
              className="btn btn-success"
              onClick={() => {
                setEditingClient(null);
                setShowForm(true);
              }}
            >
              <Plus size={18} />
              Nuevo Cliente
            </button>
          )}
        </div>

        {/* BODY */}
        <div className="card-body">
          {/* FORMULARIO */}
          {showForm && (
            <ClientForm
              editingClient={editingClient}
              setEditingClient={setEditingClient}
              fetchClients={() => fetchClients(currentPage, search)}
              onCancel={() => {
                setEditingClient(null);
                setShowForm(false);
                setTimeout(() => searchRef.current?.focus(), 50);
              }}
            />
          )}

          {/* BUSCADOR */}
          {!showForm && (
            <div className="form-group" style={{ position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre, NIT, DUI o documento..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px" }}
                />
              </div>
              {search && (
                <small style={{ color: "#718096", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  🔍 Buscando en todos los clientes...
                </small>
              )}
            </div>
          )}

          {/* TABLA */}
          {!showForm && (
            <>
              {loading ? (
                <div className="text-center" style={{ padding: "40px" }}>
                  <div className="spinner" style={{ margin: "0 auto" }}></div>
                  <p style={{ marginTop: "16px", color: "#718096" }}>
                    Cargando clientes...
                  </p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Tipo</th>
                        <th>Documento</th>
                        <th>Teléfono</th>
                        <th style={{ textAlign: "center", width: "180px" }}>
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.length === 0 ? (
                        <tr>
                          <td colSpan="5">
                            <div
                              className="text-center"
                              style={{ padding: "40px 20px" }}
                            >
                              <FileText
                                size={48}
                                color="#cbd5e0"
                                style={{ marginBottom: "16px" }}
                              />
                              <p
                                style={{
                                  color: "#a0aec0",
                                  fontSize: "16px",
                                  margin: 0,
                                }}
                              >
                                {search
                                  ? "No se encontraron clientes con ese criterio"
                                  : "No hay clientes registrados"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        clients.map((client) => (
                          <tr key={client.id}>
                            <td>
                              <span style={{ fontWeight: 600 }}>
                                {client.nombre}
                              </span>
                              {client.nombre_comercial && (
                                <div style={{ fontSize: "12px", color: "#718096" }}>
                                  {client.nombre_comercial}
                                </div>
                              )}
                            </td>
                            <td>
                              <span style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: 600,
                                background: client.tipo_cliente === "Contribuyente" ? "#edf2f7" : "#e6fffa",
                                color: client.tipo_cliente === "Contribuyente" ? "#667eea" : "#38b2ac",
                              }}>
                                {client.tipo_cliente}
                              </span>
                            </td>
                            <td>
                              {client.numero_documento ? (
                                <div>
                                  <div style={{ fontWeight: 500 }}>
                                    {client.numero_documento}
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#a0aec0" }}>
                                    {getTipoDocumentoNombre(client.tipo_documento)}
                                  </div>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{client.telefono || "-"}</td>
                            <td>
                              <div
                                className="d-flex gap-2"
                                style={{ justifyContent: "center" }}
                              >
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    setEditingClient(client);
                                    setShowForm(true);
                                  }}
                                  title="Editar cliente"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(client.id)}
                                  title="Eliminar cliente"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ✅ PAGINACIÓN */}
              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                    Página {currentPage} de {totalPages}
                  </span>

                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {/* RESUMEN */}
              {clients.length > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f7fafc",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    Mostrando {from} - {to} de {total} clientes
                  </span>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <span style={{ color: "#718096", fontSize: "14px" }}>
                      Contribuyentes:{" "}
                      <strong style={{ color: "#667eea" }}>
                        {
                          Array.isArray(clients) ? clients.filter(
                            (c) => c.tipo_cliente === "Contribuyente"
                          ).length : 0
                        }
                      </strong>
                    </span>
                    <span style={{ color: "#718096", fontSize: "14px" }}>
                      No Contribuyentes:{" "}
                      <strong style={{ color: "#4299e1" }}>
                        {
                          Array.isArray(clients) ? clients.filter(
                            (c) => c.tipo_cliente === "No Contribuyente"
                          ).length : 0
                        }
                      </strong>
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}